
import { BroadcastBaseIcon } from '@tableau/icons/BroadcastBaseIcon';
import {
    DialogPositioningBehavior, DialogProps,
    DialogType, DialogWidget
} from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { Loadable, LoadingStatus } from '~src/httpClient/utils/Loadable';

import { IBroadcastDetail } from '../models/BroadcastModels';
import * as styles from '../styles/BroadcastDialogStyle';
import { BroadcastLegalDisclaimer } from './BroadcastLegalDisclaimer';
import { BroadcastToggle } from './BroadcastToggle';
import { EmbedCodeCopyButton, EmbedCodeDropdown } from './EmbedCode';
import { InProgressAlert } from './InProgressAlert';
import { PublicFacingUrlCopyButton } from './PublicFacingUrlCopyButton';


export const BROADCAST_VIEW_DIALOG_TEST_ID = 'modal-broadcastdetail-panel';

export class BroadcastViewDetailDialog extends React.PureComponent<BroadcastViewDetailDialogProps, BroadcastViewDetailDialogStates> {

    constructor(props: BroadcastViewDetailDialogProps) {
        super(props);
        this.state = {
            isBroadcast: !!this.props.broadcastStatus.content,
            isPublishInProgress: false,
            isToggleAllowed: !!this.props.broadcastStatus.content,
            showBroadcastDetails: (this.props.broadcastDetail.loadingStatus === LoadingStatus.LOADED) && !_.isNull(this.props.broadcastDetail.content),
        };
    }

    public render() {
        const props: DialogProps = {
            onCloseRequest: this.props.onCloseRequest,
            isModal: true,
            type: DialogType.NormalWithTitleBar,
            testId: BROADCAST_VIEW_DIALOG_TEST_ID,
            showCloseButton: false,
            positioningBehavior: DialogPositioningBehavior.Centered,
            shouldAdjustPositionOnWindowResize: true,
            dialogStyle: { minWidth: 502 },
            title: Messages.broadcastDialogTitle(),
        };

        return (
            <DialogWidget {...props}
                showCloseButton
                onCloseRequest={props.onCloseRequest}
            >
                {this.getTitle()}
                <p data-tb-test-id={BROADCAST_VIEW_DIALOG_TEST_ID + '-description'}>
                    {Messages.broadcastViewDialogDescription()}
                </p>
                {!this.state.isBroadcast ?
                    this.legalDisclaimer() : null
                }
                <div
                    style={styles.toggle}
                >
                    {this.toggleBroadcast()}
                </div>
                {this.state.showBroadcastDetails ?
                    <>
                        <div>
                            {this.EmbedCodeDropdown()}
                        </div>
                        <div style={styles.buttonsContainer}>
                            {this.embedCode()}
                            {this.shareLink()}
                        </div>
                    </>
                    : null}
                {this.state.isPublishInProgress ?
                    this.triggerBroadcastInProgressAlert() : null}
            </DialogWidget>
        );
    }

    public componentDidUpdate(prevProps: BroadcastViewDetailDialogProps) {
        // update status of the finished request
        if (!_.isEqual(prevProps.broadcastStatus, this.props.broadcastStatus)
            && this.props.broadcastStatus.loadingStatus !== LoadingStatus.LOADING
        ) {
            this.setState({ isPublishInProgress: false });
        }

        // on successful request:
        // update state to display broadcast details and user can delete broadcast using toggle button
        if (!_.isEqual(prevProps.broadcastDetail, this.props.broadcastDetail)
            && this.props.broadcastDetail.loadingStatus === LoadingStatus.LOADED
            && !_.isNull(this.props.broadcastDetail.content)
        ) {
            this.setState({ showBroadcastDetails: true, isToggleAllowed: true });
        }

    }

    private handleDisclaimerButtonOnChange = (checked: boolean) => {
        this.setState({ isToggleAllowed: checked });
    };

    private triggerBroadcastInProgressAlert = (): JSX.Element => {
        return <InProgressAlert />;
    };

    private handleToggleBroadcast = () => {
        if (!this.state.isBroadcast) {
            this.props.onCreateBroadcast();
            this.setState({
                isBroadcast: !this.state.isBroadcast,
                isPublishInProgress: true,
                isToggleAllowed: false,
            });
        } else {
            this.props.onDeleteBroadcast();
            this.setState({
                isBroadcast: !this.state.isBroadcast,
                isToggleAllowed: false,
                showBroadcastDetails: false,
            });
        }
    };

    private toggleBroadcast(): JSX.Element {
        const props: BroadcastToggle.Props = {
            isBroadcast: this.state.isBroadcast,
            onToggleSwitch: this.handleToggleBroadcast,
            isToggleAllowed: this.state.isToggleAllowed
        };
        return <BroadcastToggle {...props} />;
    }

    private legalDisclaimer(): JSX.Element {
        return <BroadcastLegalDisclaimer
            checkBoxOnChange={this.handleDisclaimerButtonOnChange}
        />;
    }

    private embedCode(): JSX.Element {
        return <EmbedCodeCopyButton
            publicFacingUrl={this.props.broadcastDetail.content.publicFacingUrl}
            contentName={this.props.broadcastDetail.content.broadcastViewName}
        />;
    }
    private EmbedCodeDropdown(): JSX.Element {
        return <EmbedCodeDropdown
            publicFacingUrl={this.props.broadcastDetail.content.publicFacingUrl}
        />;
    }

    private shareLink(): JSX.Element {
        return <PublicFacingUrlCopyButton
            shareLink={this.props.broadcastDetail.content.publicFacingUrl}
            contentName={this.props.broadcastDetail.content.broadcastViewName}
        />;
    }
    private getTitle(): JSX.Element {
        return (
            <div style={styles.title}>
                <span style={styles.titleSpan}>
                    <BroadcastBaseIcon size={14}
                        style={styles.icon}
                        alt={Messages.view()} />
                    <span style={styles.contentName}>
                        {this.contentName()}
                    </span>
                </span>
            </div>
        );
    }

    private contentName = (maxLength: number = 50): string => {
        // Shorten title to a certain length, omit rest with ellipses
        const HORIZONTAL_ELLIPSIS = '\u2026';
        return this.props.viewName && this.props.viewName.length > maxLength
            ? this.props.viewName.substring(0, maxLength) + HORIZONTAL_ELLIPSIS
            : this.props.viewName;
    };
}

export interface BroadcastViewDetailDialogProps {
    readonly broadcastStatus: Loadable<boolean>;
    readonly broadcastDetail: Loadable<IBroadcastDetail>;
    readonly viewName: string;
    onCloseRequest: () => void;
    onCreateBroadcast: () => void;
    onDeleteBroadcast: () => void;
}

export interface BroadcastViewDetailDialogStates {
    isBroadcast: boolean;
    isPublishInProgress: boolean;
    isToggleAllowed: boolean;
    showBroadcastDetails: boolean;
}
